import { inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
    ActionsEnum,
    EntityEnum,
    FieldInterventionEnum,
    FilterEnum,
    LayerEnum,
} from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentLayoutService, ONE_THIRD_LAYOUT_KEY } from '@connect-services/client/services/content-layout.service';
import { PanelService } from '@connect-services/client/services/panel.service';

type Services = {
    contentLayoutService: ContentLayoutService;
    panelService: PanelService;
};

export function gridResolver(route: ActivatedRouteSnapshot): void {
    // Injections must be declared here to avoid Angular error: NG0203 about injection context
    const services: Services = {
        contentLayoutService: inject(ContentLayoutService),
        panelService: inject(PanelService),
    };

    inspectRoute(route, services);
}

function inspectRoute(route: ActivatedRouteSnapshot, services: Services): void {
    const label: RoutePathEnum = (route.routeConfig?.data?.['name'] as RoutePathEnum) ?? '';

    loadGrid(label, services);

    if (!route.firstChild) {
        return;
    }
    inspectRoute(route.firstChild, services);
}

function loadGrid(routeLabel: RoutePathEnum, services: Services): void {
    const splitRouteLabel = routeLabel.split(':').at(-1) as RoutePathEnum | undefined;

    if (splitRouteLabel) {
        services.contentLayoutService.currentRoutePath.set(splitRouteLabel);
    }

    switch (splitRouteLabel) {
        case RoutePathEnum.LAYER_FILTER:
        case RoutePathEnum.LAYER_FILTER_ID:
            services.contentLayoutService.changeContentSize(
                FilterEnum.LAYOUT_FILTER_CREATION_EDITION,
                ONE_THIRD_LAYOUT_KEY,
            );
            services.panelService.setPanel(FilterEnum.LAYOUT_FILTER_CREATION_EDITION);
            break;
        case RoutePathEnum.FIELD_INTERVENTION_PLANNING:
            services.contentLayoutService.isDataTableView.set(false);
            services.contentLayoutService.changeContentSize(
                FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING,
                ONE_THIRD_LAYOUT_KEY,
            );
            services.panelService.setPanel(FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING);
            break;
        case RoutePathEnum.FIELD_INTERVENTION_DASHBOARD:
            services.panelService.setPanel(FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD);
            break;
        case RoutePathEnum.FIELD_INTERVENTION_ID:
            services.contentLayoutService.isDataTableView.set(false);
            services.contentLayoutService.changeContentSize(
                FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD,
                ONE_THIRD_LAYOUT_KEY,
            );
            break;
        case RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID:
            services.contentLayoutService.changeContentSize(FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_DETAIL);
            services.panelService.setPanel(EntityEnum.ENTITIES_LIST);
            break;
        case RoutePathEnum.FIELD_INTERVENTIONS:
            services.contentLayoutService.changeContentSize(ActionsEnum.FIELD_INTERVENTION);
            services.panelService.setPanel(ActionsEnum.FIELD_INTERVENTION);
            break;
        case RoutePathEnum.FIELD_INTERVENTION_ENTITIES:
        case RoutePathEnum.FIELD_INTERVENTION_FORMS:
        case RoutePathEnum.FIELD_INTERVENTION_LAYERS:
            services.contentLayoutService.changeContentSize(EntityEnum.ENTITIES_LIST, ONE_THIRD_LAYOUT_KEY);
            services.panelService.setPanel(EntityEnum.ENTITIES_LIST);
            break;
        case RoutePathEnum.FIELD_INTERVENTION_LAYER_ID:
            services.contentLayoutService.changeContentSize(FieldInterventionEnum.FIELD_INTERVENTION_LAYER_DETAIL);
            services.panelService.setPanel(EntityEnum.ENTITIES_LIST);
            break;

        case RoutePathEnum.FIELD_INTERVENTION_CREATE:
            services.contentLayoutService.isDataTableView.set(false);
            services.contentLayoutService.changeContentSize(FieldInterventionEnum.FIELD_INTERVENTION_CREATE);
            services.panelService.setPanel(FieldInterventionEnum.FIELD_INTERVENTION_CREATE);
            break;
        case RoutePathEnum.STATISTICS:
            services.contentLayoutService.changeContentSize(ActionsEnum.STATISTICS, ONE_THIRD_LAYOUT_KEY);
            services.panelService.setPanel(ActionsEnum.STATISTICS);
            break;

        case RoutePathEnum.DEPOSIT_DETAIL:
        case RoutePathEnum.SETTINGS:
            services.contentLayoutService.changeContentSize(
                ActionsEnum.SETTINGS,
                services.contentLayoutService.activeLayoutKey(),
            );
            services.panelService.setPanel(ActionsEnum.SETTINGS);
            break;

        case RoutePathEnum.ENTITY_DETAIL:
            services.contentLayoutService.changeContentSize(EntityEnum.ENTITY_DETAIL);
            services.panelService.setPanel(EntityEnum.ENTITY_DETAIL);
            break;
        case RoutePathEnum.TABLE_ENTITY_ID:
            services.contentLayoutService.isDataTableView.set(true);
            services.contentLayoutService.changeContentSize(EntityEnum.TABLE_ENTITY_DETAIL);
            services.panelService.setPanel(EntityEnum.TABLE_ENTITY_DETAIL);
            break;
        case RoutePathEnum.LAYERS:
            services.contentLayoutService.changeContentSize(ActionsEnum.FIELD_INTERVENTION);
            services.panelService.setPanel(ActionsEnum.LAYER);
            break;
        case RoutePathEnum.DEPOSIT_DETAIL_ID:
        case RoutePathEnum.DETAIL_LAYER_ID:
            services.contentLayoutService.changeContentSize(LayerEnum.DEPOSIT_LAYER_DETAIL);
            services.panelService.setPanel(ActionsEnum.SETTINGS);
            break;
        case RoutePathEnum.SYNOPTIC:
            services.contentLayoutService.changeContentSize(ActionsEnum.SYNOPTIC);
            services.panelService.setPanel(ActionsEnum.SYNOPTIC);
            break;
        case RoutePathEnum.AI:
            services.contentLayoutService.changeContentSize(ActionsEnum.AI, ONE_THIRD_LAYOUT_KEY);
            services.panelService.setPanel(ActionsEnum.AI);
            break;
        case RoutePathEnum.NETWORK_PATH:
            services.contentLayoutService.changeContentSize(ActionsEnum.NETWORK_PATH);
            services.panelService.setPanel(ActionsEnum.NETWORK_PATH);
            break;
        default:
            services.contentLayoutService.changeContentSize(null);
            services.panelService.setPanel(null);
    }
}
